import * as THREE from 'three';
import TextAnnotationData from "../sceneObjectData/TextAnnotationData";
import SceneGeometry from "./SceneGeometry";
import { sceneUnitsFromMm } from '../../utils/scaler';

import { LineSegments2 } from 'three/addons/lines/LineSegments2.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';

import { TextGeometry } from 'three/addons/geometries/TextGeometry.js';
import { Font } from 'three/addons/loaders/FontLoader.js';
import FontProvider from '../../logic/FontProvider';
import { updateDoubleSidedText, createHitLine } from '../../utils/annotationUtils';
import { OBB } from 'three/addons/math/OBB.js';
import { getSceneObjectStyle, getSceneObjectStyleName, SceneObjectStyle, SceneObjectStyleEnum } from './SceneObjectStyle';

export default class TextAnnotationGeometry extends SceneGeometry<TextAnnotationData> {
    lineGeometry: LineGeometry;
    lineMaterial = TextAnnotationGeometry.getLineMaterial(getSceneObjectStyle(SceneObjectStyleEnum.Normal));
    line: LineSegments2;
    lineHitLine: LineSegments2; // Invisible, wider hit target kept in sync with line's visibility
    textGroup: THREE.Group = new THREE.Group; // Text mesh for the dimension text
    textFont: Font | undefined = undefined; // Font for the text mesh

    textMaterial = TextAnnotationGeometry.getTextMaterial(getSceneObjectStyle(SceneObjectStyleEnum.Normal));

    textGroupObb : OBB | undefined;

    private static lineMaterialCache = new WeakMap<SceneObjectStyle, LineMaterial>();
    private static textMaterialCache = new WeakMap<SceneObjectStyle, THREE.MeshBasicMaterial>();

    private static getLineMaterial(style: SceneObjectStyle): LineMaterial {
        let mat = TextAnnotationGeometry.lineMaterialCache.get(style);
        if (!mat) {
            mat = new LineMaterial({
                color: style.color,
                linewidth: style.lineWidth,
                resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
                dashed: false
            });
            mat.name = `TextAnnotation-${getSceneObjectStyleName(style)}-Line`;
            TextAnnotationGeometry.lineMaterialCache.set(style, mat);
        }
        return mat;
    }

    private static getTextMaterial(style: SceneObjectStyle): THREE.MeshBasicMaterial {
        let mat = TextAnnotationGeometry.textMaterialCache.get(style);
        if (!mat) {
            mat = new THREE.MeshBasicMaterial({ color: style.color });
            mat.name = `TextAnnotation-${getSceneObjectStyleName(style)}-Text`;
            TextAnnotationGeometry.textMaterialCache.set(style, mat);
        }
        return mat;
    }

    constructor() {
        super();

        this.lineGeometry = new LineGeometry();
        this.line = new LineSegments2(this.lineGeometry, this.lineMaterial);
        
        this.textFont = FontProvider.getInstanceSync().arial;

        this.lineHitLine = createHitLine(this.lineGeometry);

        this.group.add(this.line);
        this.group.add(this.lineHitLine);
        this.group.add(this.textGroup); // Add the text group to the main group

        //const obbMaterial = new THREE.MeshBasicMaterial({color: 0x000000, wireframe: true});
        
        //this.obbVisualizerMesh = new THREE.Mesh(new THREE.BoxGeometry(1,1,1), obbMaterial);
        //this.group.add(this.obbVisualizerMesh);
     
    }

    updateGeometry(textAnnotationDto: TextAnnotationData, forceUpdate: boolean = false) {
        
        let isUpdated = this.updateTextGeometry(textAnnotationDto) ; 
        isUpdated = this.updateLineGeometry(textAnnotationDto) || isUpdated;

        if (isUpdated) 
            this.data = textAnnotationDto.clone(); // Clone the data to avoid reference issues

        return isUpdated;

    }

    /* buildTextBB(center: THREE.Vector3, fontSize: number, text: string) : THREE.Box3 {
        const bb = new THREE.Box3();

    } */

    updateLineGeometry(textAnnotationDto: TextAnnotationData) {

        const { position, offset, fontSize } = textAnnotationDto; // Destructure the position and offset from the text annotation data

        // If we have text, find closest side middle point 
        if (this.textGroup) {
            this.textGroup.updateWorldMatrix(true, true); // Update the world matrix of the text mesh
            //const textBB = new THREE.Box3().setFromObject(this.textGroup); // Get the bounding box of the text mesh
            
            if (!this.textGroupObb)
                return;

            const textOBB = this.textGroupObb;
            
            // FOR DEBUGGING OBB
            /* this.obbVisualizerMesh.position.copy(textOBB.center);
            this.obbVisualizerMesh.geometry = new THREE.BoxGeometry(textOBB.halfSize.x*2, textOBB.halfSize.y*2, textOBB.halfSize.z*2);
            this.obbVisualizerMesh.applyMatrix4(new THREE.Matrix4().setFromMatrix3(textOBB.rotation.invert()));
             */
            //this.obbVisualizerMesh.visible = false; 

            const closestPoint = textOBB?.intersectRay(new THREE.Ray(position.clone(), offset.clone().normalize()), new THREE.Vector3());

            const isPositionInsideBb = textOBB.containsPoint(position); // Check if the position is inside the bounding box
            
            // If we have closest point and it is far enough
            if (!isPositionInsideBb && closestPoint /* && closestPoint.distanceTo(position) < offset.length() */) {
                this.line.visible = true;
                this.lineHitLine.visible = true;
                this.lineGeometry.setPositions([position,closestPoint].flatMap(v => [v.x, v.y, v.z]));
                //console.log(`Line ${this.line.id} from ${position.toArray()} to ${closestPoint.toArray()}`);
            }
            else { // Otherwise set points so there is no line to draw
                this.line.visible = false;
                this.lineHitLine.visible = false;
            }
        }
        else { // Fallback
            const p2 : THREE.Vector3 = position.clone().add(offset.clone().multiplyScalar(0.8)); // Calculate the end point of the line
            this.lineGeometry.setPositions([position, p2].flatMap(v => [v.x, v.y, v.z])); 
        }

        //this.lineGeometry.attributes.position.needsUpdate = true; // Mark the position attribute as needing an update

        return true;
    }

    updateTextGeometry(textAnnotationDto: TextAnnotationData) {

        if (textAnnotationDto.text.length > 0) {

            if (this.textFont && (this.textGroup.children.length === 0 || textAnnotationDto.text != this.data?.text || textAnnotationDto.fontSize != this.data?.fontSize))
                updateDoubleSidedText(textAnnotationDto.text, this.textFont, textAnnotationDto.fontSize, this.textGroup, this.textMaterial); // Update the text mesh with the new text or font size

            this.textGroup.visible = true; // Show the text mesh if there is text
            this.textGroup.position.copy(textAnnotationDto.position.clone().add(textAnnotationDto.offset));

            const position = this.textGroup.position.clone();
            const target = position.clone().add(textAnnotationDto.textNormal.clone());
            const lookAtMatrix = new THREE.Matrix4().lookAt(this.textGroup.position.clone(), target, textAnnotationDto.textUp);
            
            // Set the quaternion from the lookAt matrix
            this.textGroup.quaternion.setFromRotationMatrix(lookAtMatrix);

            // Compute the OBB from the geometry's local bounding box.
            // Using setFromObject would include parent world-matrix rotation, inflating the AABB
            // when the annotation is a child of a rotated building element.
            // Instead, read the tight bounds directly from the ShapeGeometry (which is always centered
            // in local XY space by geometry.center()) and apply the textGroup's own quaternion as the
            // OBB rotation so the box correctly represents the oriented text plane.
            let textHalfSize: THREE.Vector3 | null = null;
            this.textGroup.traverse(child => {
                if (!textHalfSize && child instanceof THREE.Mesh && child.name === 'FrontTextMesh') {
                    child.geometry.computeBoundingBox();
                    const geomBB = child.geometry.boundingBox;
                    if (geomBB) {
                        textHalfSize = geomBB.getSize(new THREE.Vector3()).divideScalar(2);
                        if (textHalfSize !== null)
                            textHalfSize.z = Math.max(textHalfSize.z, 0.02); // Add depth so ray hits
                    }
                }
            });

            if (textHalfSize) {
                const obbRotation = new THREE.Matrix3().setFromMatrix4(
                    new THREE.Matrix4().makeRotationFromQuaternion(this.textGroup.quaternion)
                );
                this.textGroupObb = new OBB(
                    this.textGroup.position.clone(),
                    new THREE.Vector3().copy(textHalfSize).addScalar(sceneUnitsFromMm(10)),
                    obbRotation
                );
            }
        }
        else {
            this.textGroup.visible = false; // Hide the text mesh if there is no text
        }

        return true;
    }

    getTextGeometry(text: string, fontSize: number): TextGeometry {
            if (!this.textFont) {
                throw new Error("Font not loaded yet.");
            }
            return new TextGeometry(text, {
                font: this.textFont,
                size: fontSize,
                depth: 0.2,
                curveSegments: 6,
                bevelEnabled: false,
            });
        }

    setStyle(style: SceneObjectStyle): void {
        if (this.style === style) return;
        const lineMat = TextAnnotationGeometry.getLineMaterial(style);
        const textMat = TextAnnotationGeometry.getTextMaterial(style);
        this.line.material = lineMat;
        this.lineMaterial = lineMat;
        this.textMaterial = textMat;
        this.textGroup.traverse((child) => {
            if (child instanceof THREE.Mesh && child.name !== "HitBoxMesh") {
                child.material = textMat;
            }
        });
        this.style = style;
    }

    dispose(): void {
        this.group.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                child.geometry.dispose();
            }
        });
        this.group.parent?.remove(this.group);
        this.group.clear();
    }

}